import {
  DoubleSide,
  LinearFilter,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  Texture
} from "three";
import type { DxfBatchSettingRegion } from "./batch-setting-region-layout";
import type { DxfDevicePlacement } from "./cad-device-layout";
import type { DxfDoubleChainConveyorPlacement } from "./double-chain-conveyor-layout";
import type {
  DxfBinRackPlacementResult,
  DxfRackPlacement,
  DxfRasterResult,
  DxfRasterStage,
  DxfRasterWorkerMessage
} from "./dxf-raster.worker";
import type { DxfPalletReciprocatingLiftPlacement } from "./pallet-reciprocating-lift-layout";
import type { DxfPalletStackerCranePlacement } from "./pallet-stacker-crane-layout";
import type { DxfRollerConveyorPlacement } from "./roller-conveyor-layout";
import type { DxfRollerLiftTransferPlacement } from "./roller-lift-transfer-layout";
import type { DxfSingleStationReciprocatingShuttlePlacement } from "./single-station-reciprocating-shuttle-layout";

export type DxfFloorReferenceStage = "reading" | DxfRasterStage;

export type DxfFloorReferenceMetadata = Omit<
  DxfRasterResult,
  | "batchSettingRegions"
  | "binRackPlacements"
  | "bitmap"
  | "devicePlacements"
  | "doubleChainConveyorPlacements"
  | "palletReciprocatingLiftPlacements"
  | "rackPlacements"
  | "rollerConveyorPlacements"
  | "rollerLiftTransferPlacements"
  | "singleStationReciprocatingShuttlePlacements"
  | "palletStackerCranePlacements"
> & {
  batchSettingRegionCount: number;
  binRackCount: number;
  doubleChainConveyorCount: number;
  palletRackCount: number;
  palletReciprocatingLiftCount: number;
  rackCount: number;
  rollerConveyorCount: number;
  rollerLiftTransferCount: number;
  singleStationReciprocatingShuttleCount: number;
  palletStackerCraneCount: number;
};

export type DxfFloorReference = {
  batchSettingRegions: DxfBatchSettingRegion[];
  binRackPlacements: DxfBinRackPlacementResult[];
  devicePlacements: DxfDevicePlacement[];
  doubleChainConveyorPlacements: DxfDoubleChainConveyorPlacement[];
  dispose: () => void;
  mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
  metadata: DxfFloorReferenceMetadata;
  palletReciprocatingLiftPlacements: DxfPalletReciprocatingLiftPlacement[];
  rackPlacements: DxfRackPlacement[];
  rollerConveyorPlacements: DxfRollerConveyorPlacement[];
  rollerLiftTransferPlacements: DxfRollerLiftTransferPlacement[];
  singleStationReciprocatingShuttlePlacements: DxfSingleStationReciprocatingShuttlePlacement[];
  palletStackerCranePlacements: DxfPalletStackerCranePlacement[];
};

type CreateDxfFloorReferenceOptions = {
  anisotropy: number;
  deviceBlockPrefixes: string[];
  maxTextureSize: number;
  onStage?: (stage: DxfFloorReferenceStage) => void;
  signal?: AbortSignal;
};

export async function createDxfFloorReference(
  file: File,
  options: CreateDxfFloorReferenceOptions
): Promise<DxfFloorReference> {
  if (!file.name.toLowerCase().endsWith(".dxf")) {
    throw new Error("请选择 .dxf 文件");
  }

  options.onStage?.("reading");
  const buffer = await file.arrayBuffer();
  throwIfAborted(options.signal);
  const result = await rasterizeDxf(buffer, options);
  throwIfAborted(options.signal, result.bitmap);

  const texture = new Texture(result.bitmap);
  texture.colorSpace = SRGBColorSpace;
  texture.flipY = false;
  texture.generateMipmaps = true;
  texture.magFilter = LinearFilter;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = Math.max(1, options.anisotropy);
  texture.premultiplyAlpha = true;
  texture.needsUpdate = true;

  const geometry = new PlaneGeometry(result.worldWidth, result.worldDepth);
  const material = new MeshBasicMaterial({
    depthWrite: false,
    map: texture,
    opacity: 0.72,
    side: DoubleSide,
    transparent: true
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = "dxf-floor-reference";
  mesh.position.y = 0.001;
  mesh.renderOrder = -10;
  mesh.rotation.x = -Math.PI / 2;
  mesh.castShadow = false;
  mesh.receiveShadow = false;

  let disposed = false;
  return {
    batchSettingRegions: result.batchSettingRegions,
    binRackPlacements: result.binRackPlacements,
    devicePlacements: result.devicePlacements,
    doubleChainConveyorPlacements: result.doubleChainConveyorPlacements,
    dispose() {
      if (disposed) {
        return;
      }

      disposed = true;
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
      texture.dispose();
      result.bitmap.close();
    },
    mesh,
    metadata: {
      batchSettingRegionCount: result.batchSettingRegions.length,
      centerX: result.centerX,
      centerY: result.centerY,
      binRackCount: result.binRackPlacements.length,
      doubleChainConveyorCount: result.doubleChainConveyorPlacements.length,
      isUnitFallback: result.isUnitFallback,
      pixelHeight: result.pixelHeight,
      pixelWidth: result.pixelWidth,
      palletRackCount: result.rackPlacements.length,
      palletReciprocatingLiftCount: result.palletReciprocatingLiftPlacements.length,
      rackCount: result.rackPlacements.length + result.binRackPlacements.length,
      rollerConveyorCount: result.rollerConveyorPlacements.length,
      rollerLiftTransferCount: result.rollerLiftTransferPlacements.length,
      singleStationReciprocatingShuttleCount:
        result.singleStationReciprocatingShuttlePlacements.length,
      palletStackerCraneCount: result.palletStackerCranePlacements.length,
      unitLabel: result.unitLabel,
      worldDepth: result.worldDepth,
      worldWidth: result.worldWidth
    },
    palletReciprocatingLiftPlacements: result.palletReciprocatingLiftPlacements,
    rackPlacements: result.rackPlacements,
    rollerConveyorPlacements: result.rollerConveyorPlacements,
    rollerLiftTransferPlacements: result.rollerLiftTransferPlacements,
    singleStationReciprocatingShuttlePlacements: result.singleStationReciprocatingShuttlePlacements,
    palletStackerCranePlacements: result.palletStackerCranePlacements
  };
}

function rasterizeDxf(
  buffer: ArrayBuffer,
  options: CreateDxfFloorReferenceOptions
): Promise<DxfRasterResult> {
  const worker = new Worker(new URL("./dxf-raster.worker.ts", import.meta.url), {
    name: "dxf-raster",
    type: "module"
  });

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = (callback: () => void) => {
      if (settled) {
        return;
      }

      settled = true;
      options.signal?.removeEventListener("abort", handleAbort);
      worker.terminate();
      callback();
    };

    const handleAbort = () => {
      finish(() => reject(createAbortError()));
    };

    worker.onmessage = (event: MessageEvent<DxfRasterWorkerMessage>) => {
      const message = event.data;
      if (message.type === "stage") {
        options.onStage?.(message.stage);
        return;
      }
      if (message.type === "error") {
        finish(() => reject(new Error(message.message)));
        return;
      }

      finish(() => resolve(message));
    };

    worker.onerror = (event) => {
      finish(() => reject(new Error(event.message || "DXF 图纸 Worker 运行失败")));
    };

    options.signal?.addEventListener("abort", handleAbort, { once: true });
    worker.postMessage(
      {
        buffer,
        deviceBlockPrefixes: options.deviceBlockPrefixes,
        maxTextureSize: options.maxTextureSize
      },
      [buffer]
    );
  });
}

function throwIfAborted(signal?: AbortSignal, bitmap?: ImageBitmap) {
  if (!signal?.aborted) {
    return;
  }

  bitmap?.close();
  throw createAbortError();
}

function createAbortError() {
  return new DOMException("DXF 图纸加载已取消", "AbortError");
}
